import { DOCUMENT } from '@angular/common';
import { computed, effect, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { generateUUID } from '@core';
import { RunDraft, RunInput, SessionDraft, SessionDrillDraft, SessionDrillDraftInit } from './session-store.types';

const STORAGE_KEY: string = 'splitzero.session.active';
const AUTOSAVE_DEBOUNCE_MS: number = 200;

/** Limit z walidacji API (`createSessionSchema`: `sessionDrills.max(50)`). */
export const MAX_SESSION_DRILLS: number = 50;

@Injectable({ providedIn: 'root' })
export class SessionStore {
  private readonly document: Document = inject(DOCUMENT);
  private readonly storage: Storage | null = this.document.defaultView?.localStorage ?? null;
  private readonly _draft: WritableSignal<SessionDraft | null>;

  readonly draft: Signal<SessionDraft | null>;
  readonly sessionDrills: Signal<readonly SessionDrillDraft[]>;
  readonly activeIndex: Signal<number>;
  readonly activeDrill: Signal<SessionDrillDraft | null>;
  readonly isMultiDrill: Signal<boolean>;
  readonly isLastDrill: Signal<boolean>;
  readonly canAddDrill: Signal<boolean>;

  private autosaveTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this._draft = signal<SessionDraft | null>(this.loadFromStorage());

    this.draft = this._draft.asReadonly();
    this.sessionDrills = computed(() => this._draft()?.sessionDrills ?? []);
    this.activeIndex = computed(() => this._draft()?.activeIndex ?? 0);
    this.activeDrill = computed(() => {
      const drills: readonly SessionDrillDraft[] = this.sessionDrills();
      const index: number = this.activeIndex();
      return drills.length === 0 || index >= drills.length ? null : drills[index];
    });
    this.isMultiDrill = computed(() => this.sessionDrills().length > 1);
    this.isLastDrill = computed(() => {
      const drills: readonly SessionDrillDraft[] = this.sessionDrills();
      return drills.length === 0 || this.activeIndex() >= drills.length - 1;
    });
    this.canAddDrill = computed(() => this._draft() !== null && this.sessionDrills().length < MAX_SESSION_DRILLS);

    effect(() => {
      const current: SessionDraft | null = this._draft();
      this.scheduleAutosave(current);
    });
  }

  snapshot(): SessionDraft | null {
    return this._draft();
  }

  initDraft(
    sessionDrills: readonly SessionDrillDraftInit[],
    planId: string | null = null,
    planTitle: string | null = null
  ): void {
    this._draft.set({
      clientGeneratedId: generateUUID(),
      startedAt: new Date().toISOString(),
      planId,
      planTitle,
      activeIndex: 0,
      sessionDrills: sessionDrills.map((sd: SessionDrillDraftInit) => this.toSessionDrillDraft(sd, false)),
      pendingSubmit: false,
      lastSubmitAttemptAt: null
    });
  }

  /** Dopisuje drill tuż za bieżącą pozycją - staje się następnym do wykonania (ADR-0018). */
  addDrill(init: SessionDrillDraftInit): void {
    const current: SessionDraft | null = this._draft();
    if (current === null) return;
    if (current.sessionDrills.length >= MAX_SESSION_DRILLS) return;
    const sessionDrills: SessionDrillDraft[] = [...current.sessionDrills];
    sessionDrills.splice(current.activeIndex + 1, 0, this.toSessionDrillDraft(init, true));
    this._draft.set({ ...current, sessionDrills });
  }

  /** Usuwa dopisany drill, o ile nie został rozpoczęty (za bieżącą pozycją, bez przebiegów). */
  removeAddedDrill(instanceId: string): void {
    const current: SessionDraft | null = this._draft();
    if (current === null) return;
    const index: number = current.sessionDrills.findIndex((sd: SessionDrillDraft) => sd.instanceId === instanceId);
    if (index <= current.activeIndex) return;
    const drill: SessionDrillDraft = current.sessionDrills[index];
    if (!drill.addedInSession || drill.runs.length > 0) return;
    this._draft.set({
      ...current,
      sessionDrills: current.sessionDrills.filter((sd: SessionDrillDraft) => sd.instanceId !== instanceId)
    });
  }

  markSubmitPending(): void {
    const current: SessionDraft | null = this._draft();
    if (current === null) return;
    this._draft.set({ ...current, pendingSubmit: true, lastSubmitAttemptAt: new Date().toISOString() });
  }

  clearSubmitPending(): void {
    const current: SessionDraft | null = this._draft();
    if (current === null) return;
    this._draft.set({ ...current, pendingSubmit: false });
  }

  advanceToNextDrill(): void {
    const current: SessionDraft | null = this._draft();
    if (current === null) return;
    if (current.activeIndex >= current.sessionDrills.length - 1) return;
    this._draft.set({ ...current, activeIndex: current.activeIndex + 1 });
  }

  goBackToPreviousDrill(): void {
    const current: SessionDraft | null = this._draft();
    if (current === null) return;
    if (current.activeIndex <= 0) return;
    this._draft.set({ ...current, activeIndex: current.activeIndex - 1 });
  }

  addRun(instanceId: string, input: RunInput): void {
    const current: SessionDraft | null = this._draft();
    if (current === null) return;
    const run: RunDraft = this.toRunDraft(input);
    const sessionDrills: readonly SessionDrillDraft[] = current.sessionDrills.map((sd: SessionDrillDraft) =>
      sd.instanceId === instanceId ? { ...sd, runs: [...sd.runs, run] } : sd
    );
    this._draft.set({ ...current, sessionDrills });
  }

  updateRun(runId: string, input: RunInput): void {
    const current: SessionDraft | null = this._draft();
    if (current === null) return;
    const sessionDrills: readonly SessionDrillDraft[] = current.sessionDrills.map((sd: SessionDrillDraft) => ({
      ...sd,
      runs: sd.runs.map((r: RunDraft) => (r.id === runId ? this.applyRunInput(r, input) : r))
    }));
    this._draft.set({ ...current, sessionDrills });
  }

  deleteRun(runId: string): void {
    const current: SessionDraft | null = this._draft();
    if (current === null) return;
    const sessionDrills: readonly SessionDrillDraft[] = current.sessionDrills.map((sd: SessionDrillDraft) => ({
      ...sd,
      runs: sd.runs.filter((r: RunDraft) => r.id !== runId)
    }));
    this._draft.set({ ...current, sessionDrills });
  }

  clear(): void {
    this._draft.set(null);
  }

  private toSessionDrillDraft(init: SessionDrillDraftInit, addedInSession: boolean): SessionDrillDraft {
    return { ...init, instanceId: generateUUID(), addedInSession };
  }

  private toRunDraft(input: RunInput): RunDraft {
    return {
      id: generateUUID(),
      recordedAt: new Date().toISOString(),
      reps: input.reps ?? null,
      timeSeconds: input.timeSeconds ?? null,
      parTimeSeconds: input.parTimeSeconds ?? null,
      hitsA: input.hitsA ?? null,
      hitsC: input.hitsC ?? null,
      hitsD: input.hitsD ?? null,
      hitsM: input.hitsM ?? null,
      hitsNS: input.hitsNS ?? null
    };
  }

  private applyRunInput(run: RunDraft, input: RunInput): RunDraft {
    return {
      ...run,
      reps: input.reps !== undefined ? input.reps : run.reps,
      timeSeconds: input.timeSeconds !== undefined ? input.timeSeconds : run.timeSeconds,
      parTimeSeconds: input.parTimeSeconds !== undefined ? input.parTimeSeconds : run.parTimeSeconds,
      hitsA: input.hitsA !== undefined ? input.hitsA : run.hitsA,
      hitsC: input.hitsC !== undefined ? input.hitsC : run.hitsC,
      hitsD: input.hitsD !== undefined ? input.hitsD : run.hitsD,
      hitsM: input.hitsM !== undefined ? input.hitsM : run.hitsM,
      hitsNS: input.hitsNS !== undefined ? input.hitsNS : run.hitsNS
    };
  }

  private loadFromStorage(): SessionDraft | null {
    if (this.storage === null) return null;
    const raw: string | null = this.storage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    try {
      const parsed: SessionDraft = JSON.parse(raw) as SessionDraft;
      return {
        ...parsed,
        // Migracja starszych szkiców: uzupełnia instanceId (ADR-0001)
        // i addedInSession (ADR-0018) dla pozycji zapisanych przed tymi zmianami.
        sessionDrills: (parsed.sessionDrills ?? []).map((sd: SessionDrillDraft) => ({
          ...sd,
          instanceId: sd.instanceId ?? generateUUID(),
          addedInSession: sd.addedInSession ?? false
        })),
        pendingSubmit: parsed.pendingSubmit ?? false,
        lastSubmitAttemptAt: parsed.lastSubmitAttemptAt ?? null
      };
    } catch {
      this.storage.removeItem(STORAGE_KEY);
      return null;
    }
  }

  private scheduleAutosave(draft: SessionDraft | null): void {
    if (this.storage === null) return;
    if (this.autosaveTimer !== null) {
      clearTimeout(this.autosaveTimer);
    }
    this.autosaveTimer = setTimeout(() => {
      this.autosaveTimer = null;
      if (draft === null) {
        this.storage?.removeItem(STORAGE_KEY);
      } else {
        this.storage?.setItem(STORAGE_KEY, JSON.stringify(draft));
      }
    }, AUTOSAVE_DEBOUNCE_MS);
  }
}
